import React from 'react'
import { useTranslation } from 'react-i18next'
import { NestedValue, useForm } from 'react-hook-form'
import { Button, DialogActions, DialogContent } from '@material-ui/core'
import { useReactiveVar } from '@apollo/client'
import { saveAs } from 'file-saver'
import { FormGroup } from '../form/FormGroup'
import { Dialog } from '../dialog/Dialog'
import { ProjectVersionFragment } from '../../generated/graphql'
import { ControlledMultiProviderPicker } from '../form/ControlledMultiProviderPicker'
import { createVersionExport } from '../../project-versions/create-version-export'
import {
  calculatorVar,
  dictionariesVar,
  projectVar,
} from '../../apollo/reactive-vars'

export type InternalFormValues = {
  providerIds: NestedValue<string[]>
}

export type ExportFormValues = {
  providerIds: string[]
}

type Props = {
  isOpen: boolean
  activeVersion: ProjectVersionFragment
  onClose: () => void
}

export function ExportDialog({
  isOpen,
  activeVersion,
  onClose,
}: Props): React.ReactElement {
  const { control, getValues, errors } = useForm<InternalFormValues>({
    defaultValues: { providerIds: [] },
  })
  const { t } = useTranslation()
  const project = useReactiveVar(projectVar)
  const calculator = useReactiveVar(calculatorVar)
  const { containerIdsToNames } = useReactiveVar(dictionariesVar)

  const onExportVersion = async () => {
    if (!calculator) {
      return
    }

    const { providerIds } = getValues()
    const workbook = createVersionExport(
      calculator,
      providerIds,
      containerIdsToNames,
      t,
    )

    const buffer = await workbook.xlsx.writeBuffer()

    const blob = new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    })

    const fileName = `${project?.name ?? ''}-${activeVersion.name}-export.xlsx`
    saveAs(blob, fileName)

    onClose()
  }

  const containers = [...activeVersion.buildings, ...activeVersion.localGrids]

  return (
    <Dialog
      title={t('project.exportDialogTitle')}
      open={isOpen}
      onClose={onClose}
    >
      <DialogContent>
        <FormGroup>
          <ControlledMultiProviderPicker
            control={control}
            defaultValue={getValues().providerIds}
            name="providerIds"
            error={errors.providerIds}
            containers={containers}
          />
        </FormGroup>
      </DialogContent>
      <DialogActions>
        <Button variant="contained" onClick={onClose}>
          {t('common.cancel')}
        </Button>
        <Button variant="contained" color="primary" onClick={onExportVersion}>
          {t('project.exportDialogExport')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
